/**
 * FlowsList — /crm/settings/flows. Every automation flow in the workspace with status,
 * published version, and live (active) enrollment count. Admins create new
 * draft flows; the starter flows ("New Lead Welcome", "Respond Options
 * Starter") are seeded via the System page's test tools.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Plus, Workflow, X } from 'lucide-react';
import api from '../../../services/api';
import { CrmFlow } from '../../types';
import { relTime, useIsCRMAdmin } from '../../lib';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

function statusPill(status: string): string {
  switch (status) {
    case 'active': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
    case 'archived': return 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400';
    default: return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
  }
}

export default function FlowsList() {
  const navigate = useNavigate();
  const isAdmin = useIsCRMAdmin();
  const [flows, setFlows] = useState<CrmFlow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/crm/flows');
      setFlows(res.data?.flows || []);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const name = newName.trim();
    if (!name || creating) return;
    setCreating(true);
    setError(null);
    try {
      const res = await api.post('/api/crm/flows', { name });
      navigate(`/crm/settings/flows/${res.data.flow.id}`);
    } catch (e: any) {
      setError(errMsg(e));
      setCreating(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="inline-flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
            <Workflow className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            Flows
          </h1>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
            Automated outreach for new leads — texts, waits, and follow-up routing, built step by step.
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setNewOpen(true)}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 sm:min-h-0"
          >
            <Plus className="h-4 w-4" /> New flow
          </button>
        )}
      </div>

      {error && (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          <span>{error}</span>
          <button
            onClick={load}
            className="min-h-[44px] shrink-0 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-700 transition-colors duration-150 hover:bg-red-100 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/40 sm:min-h-0"
          >
            Retry
          </button>
        </div>
      )}

      {/* Inline create */}
      {newOpen && (
        <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-center gap-2">
            <input
              type="text"
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') create(); }}
              placeholder="Flow name (e.g. New lead follow-up)"
              className="min-h-[44px] flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 sm:min-h-0"
            />
            <button
              onClick={create}
              disabled={creating || !newName.trim()}
              className="min-h-[44px] rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
            >
              {creating ? 'Creating…' : 'Create'}
            </button>
            <button
              onClick={() => setNewOpen(false)}
              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 sm:min-h-0 sm:min-w-0"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Flow list */}
      <div className="mt-5">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
          </div>
        ) : error ? (
          // The red box above carries the message + Retry — never render the
          // "No flows yet" empty state when the truth is the list failed to load.
          null
        ) : flows.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center dark:border-gray-600">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No flows yet. {isAdmin ? 'Create one to automate lead follow-up.' : 'A CRM admin can create the first one.'}
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {flows.map((f) => (
                <button
                  key={f.id}
                  onClick={() => navigate(`/crm/settings/flows/${f.id}`)}
                  className="flex min-h-[44px] w-full items-center justify-between gap-3 px-4 py-3.5 text-left transition-colors duration-100 hover:bg-gray-50 dark:hover:bg-gray-700/50 sm:min-h-0 sm:px-5"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{f.name}</span>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${statusPill(f.status)}`}>
                        {f.status}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-gray-400">
                      {f.version > 0 ? `v${f.version}` : 'never published'}
                      {` · updated ${relTime(f.updated_at)}`}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{f.counts?.active_enrollments ?? 0}</p>
                    <p className="text-[11px] text-gray-400">in flow now</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
